import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { API_ENDPOINTS } from '../config/api.config';
import {
  Usuario,
  UsuarioCreatePayload,
  UsuarioUpdatePayload,
} from '../models/usuario.model';

@Injectable({
  providedIn: 'root',
})
export class UsuarioService {
  private readonly apiUrl = API_ENDPOINTS.usuarios;

  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  constructor(private readonly http: HttpClient) {}

  getByEmpresa(empresaId: number): Observable<Usuario[]> {
    return this.http
      .get<
        Usuario[]
      >(`${this.apiUrl}/super-usuario/empresa/${empresaId}/ver-lista-empleado`, this.httpOptions)
      .pipe(
        map((usuarios) =>
          (Array.isArray(usuarios) ? usuarios : []).map((usuario) =>
            this.normalizeUsuario(usuario),
          ),
        ),
        catchError(this.handleError),
      );
  }

  createByEmpresa(
    empresaId: number,
    payload: UsuarioCreatePayload,
  ): Observable<Usuario> {
    return this.http
      .post<Usuario>(
        `${this.apiUrl}/super-usuario/empresa/${empresaId}/crear-empleado`,
        payload,
        this.httpOptions,
      )
      .pipe(
        map((usuario) => this.normalizeUsuario(usuario)),
        catchError(this.handleError),
      );
  }

  updateByEmpresa(
    empresaId: number,
    usuarioId: number,
    payload: UsuarioUpdatePayload,
  ): Observable<Usuario> {
    return this.http
      .put<Usuario>(
        `${this.apiUrl}/super-usuario/empresa/${empresaId}/editar-empleado/${usuarioId}`,
        payload,
        this.httpOptions,
      )
      .pipe(
        map((usuario) => this.normalizeUsuario(usuario)),
        catchError(this.handleError),
      );
  }

  deleteByEmpresa(empresaId: number, usuarioId: number): Observable<unknown> {
    return this.http
      .delete(
        `${this.apiUrl}/super-usuario/empresa/${empresaId}/eliminar-empleado/${usuarioId}`,
        this.httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  private normalizeUsuario(usuario: any): Usuario {
    return {
      id: Number(usuario?.id ?? 0),
      nombre_completo: usuario?.nombre_completo ?? '',
      email: usuario?.email ?? '',
      telefono: usuario?.telefono ?? null,
      permisos: Array.isArray(usuario?.permisos) ? usuario.permisos : [],
      deleted_at: usuario?.deleted_at ?? null,
    };
  }

  private handleError(error: any): Observable<never> {
    const message =
      error?.error?.message || error?.message || 'Error en gestion de usuarios';
    return throwError(() => new Error(message));
  }
}
